import { wallClockSleep } from "../utils/wallClockSleep";
import { BootDataError } from "./BootDataError";
import { BootHttpError } from "./BootHttpError";
import type { Log } from "@slot/presentation";

export type RetryState = {
  taskName: string;
  attempt: number;
  nextDelayMs: number;
  errorMessage: string;
};

/** The attempts a boot load gets before its failure reaches the boot boundary (user, 2026-09-13). */
export const BOOT_RETRY_ATTEMPTS = 3;

/** The longest a 429's named wait is honoured (decision 81): the backend's windows are a minute. */
const RETRY_AFTER_MAX_MS = 60_000;

/**
 * Runs `run` up to `maxAttempts` times, backing off through `delaysMs` (the last delay repeats);
 * every failure is logged and reported. The last failure is rethrown, and so are, on their first
 * occurrence, a `BootDataError` (a payload that failed validation does not improve on a second
 * fetch) and a `BootHttpError` with a 4xx other than 429 (decision 81: the backend would refuse the
 * same request the same way). A 429 waits the time the backend named, up to a minute, instead of
 * the backoff step.
 */
export async function retryWithBackoff<T>(input: {
  taskName: string;
  run: () => Promise<T>;
  log?: Log;
  onRetrying?: (state: RetryState) => void;
  delaysMs?: number[];
  maxAttempts?: number;
}): Promise<T> {
  const delays = input.delaysMs ?? [300, 600, 1000];
  const maxAttempts = input.maxAttempts ?? BOOT_RETRY_ATTEMPTS;
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await input.run();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (error instanceof BootDataError) {
        input.log?.(`[Retry] ${input.taskName} failed on its data, not retried: ${errorMessage}`);
        throw error;
      }
      if (error instanceof BootHttpError && error.status < 500 && error.status !== 429) {
        input.log?.(`[Retry] ${input.taskName} refused (${error.status}), not retried: ${errorMessage}`);
        throw error;
      }
      if (attempt >= maxAttempts) {
        input.log?.(`[Retry] ${input.taskName} failed attempt=${attempt}/${maxAttempts}, giving up: ${errorMessage}`);
        throw error;
      }
      const limitedFor = error instanceof BootHttpError && error.status === 429 ? error.retryAfterMs : null;
      const nextDelayMs = limitedFor !== null ? Math.min(limitedFor, RETRY_AFTER_MAX_MS) : delays[Math.min(attempt - 1, delays.length - 1)]!;
      input.log?.(`[Retry] ${input.taskName} failed attempt=${attempt}/${maxAttempts} nextDelayMs=${nextDelayMs} error=${errorMessage}`);
      input.onRetrying?.({ taskName: input.taskName, attempt, nextDelayMs, errorMessage });
      await wallClockSleep(nextDelayMs);
    }
  }
}
